// =================================================================================================
import { ReactNode, PureComponent, ReactElement } from 'react';
import _ from 'lodash';
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { DebugEx } from '@Utils/DebugEx';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing, IWidgetDataValidateResp } from '@UI/Widgets/WidgetStructs';
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { SettingsList } from '@Primitive/SettingsList';
import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { WebThing, WebThingPort } from '@API/WebThing';
import { StateStatus, WebThingPortType } from '@API/WebNodeTypes';
import { Plegma } from '@API/Api';
import { WebNode } from '@API/WebNode';
import "./ForgeViewer.less";
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
// =================================================================================================
enum PortKeys {
    Settings = 'Settings',
    Selection = 'Selection',
    Drawing = 'Drawing'
}
// -------------------------------------------------------------------------------------------------
export interface ForgeViewerData {
    Document: string;
}
// -------------------------------------------------------------------------------------------------
(window as any).forgeLibraryLoaded = false;
// -------------------------------------------------------------------------------------------------
export class ForgeViewer extends PureComponent<WidgetsThingsStructs.PartialIWidgetThingsProps<ForgeViewerData>> {
    private viewer: Autodesk.Viewing.GuiViewer3D = null;
    private drawingData: Drawing;
    // ---------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<ForgeViewerData>) {
        super(props);
    }
    // ---------------------------------------------------------------------------------------------
    loadScript(src: string, cb: () => void) {
        var script = document.createElement('script');
        script.onload = cb;
        script.src = src;
        document.head.appendChild(script);
    }
    // ---------------------------------------------------------------------------------------------
    loadCSS(href: string) {
        var fileref = document.createElement("link");
        fileref.rel = "stylesheet";
        fileref.type = "text/css";
        fileref.href = href;
        document.getElementsByTagName("head")[0].appendChild(fileref)
    }
    // ---------------------------------------------------------------------------------------------
    componentDidMount() {
        if (!(window as any).forgeLibraryLoaded) {
            (window as any).forgeLibraryLoaded = true;
            this.loadCSS("https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/style.min.css");
            this.loadScript("/Content/js/forge/viewer3D.min.js?v=7.76.0", () => {
                this.SetupViewer();
            });
        } else {
            this.SetupViewer();
        }
    }
    // ---------------------------------------------------------------------------------------------
    private SetupViewer() {
        try {
            var myViewerDiv = document.getElementById('MyViewerDiv');
            this.viewer = new Autodesk.Viewing.GuiViewer3D(myViewerDiv, {
                theme: 'dark-theme',
                loaderExtensions: { svf: "Autodesk.MemoryLimited" }
            });

            var options: Autodesk.Viewing.InitializerOptions = {
                'env': 'Local',
                'document': this.props.WidgetData.Document, // TODO: make the path dynamic or setting
                useADP: false,
                //extensions: ['EmeraldExtension'],
                createWireframe: false,
                useConsolidation: true,
                consolidationMemoryLimit: 150 * 1024 * 1024
            };

            const events = [
                this.viewerEvent(Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT),
                this.viewerEvent(Autodesk.Viewing.GEOMETRY_LOADED_EVENT)
            ];

            Promise.all(events).then(() => {
                DebugEx.TraceLog("Viewer loaded");

                this.viewer.loadExtension('Autodesk.Viewing.SceneBuilder');
                this.viewer.loadExtension("Autodesk.NPR");

                // Set selection mode to first object to create selection 
                this.viewer.setSelectionMode(Autodesk.Viewing.SelectionMode.LEAF_OBJECT);

                // Register events
                //this.viewer.addEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, this.onSelectionEvent);
                this.viewer.addEventListener(Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT, this.onSelectionEvent);
                this.viewer.addEventListener(Autodesk.Viewing.MODEL_ROOT_LOADED_EVENT, (event) => {
                    console.error(event);
                })
                this.viewer.addEventListener(Autodesk.Viewing.MODEL_LAYERS_LOADED_EVENT, (event) => {
                    console.error(event);
                })
            });

            Autodesk.Viewing.Initializer(options, () => {
                (this.viewer as any).start(options.document, { isAEC: true });
            });
        } catch (ex) {
            DebugEx.TraceError('Viewer Initialization Error: ', ex);
        }
    }
    // ---------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: WidgetsThingsStructs.PartialIWidgetThingsProps<ForgeViewerData>) {
        if (this.props.PortVersion != prevProps.PortVersion) {
            const drawingPort = this.props.PortsStates[PortKeys.Drawing];
            if (drawingPort.LastStateStatus == StateStatus.Valid)
                this.setData(drawingPort.State)
        }
    }
    // ---------------------------------------------------------------------------------------------
    private async setData(data: Drawing) {
        let THREE = await import("three");

        let ext: any = this.viewer.getExtension('Autodesk.Viewing.SceneBuilder');
        let modelBuilder = await ext.addNewModel({
            conserveMemory: false,
            modelNameOverride: 'Planogram-xxxx'
        });

        this.drawingData = data;

        _.forEach(data.products,
            (p, i) => {
                var map = THREE.ImageUtils.loadTexture(p.image);
                map.wrapS = map.wrapT = THREE.ClampToEdgeWrapping;
                map.anisotropy = 16;
                map.minFilter = THREE.NearestFilter;
                const material = new THREE.MeshPhongMaterial({ map: map, side: THREE.DoubleSide } as any);

                var geom = (new THREE.BufferGeometry() as any)?.fromGeometry(new THREE.BoxGeometry(p.size.x, p.size.y, p.size.z));
                const transform = new THREE.Matrix4().compose(
                    new THREE.Vector3(data.bounds.min.x + p.offset.x, data.bounds.min.y + p.offset.y, data.bounds.min.z + p.offset.z),
                    new THREE.Quaternion(0, 0, 0, 1),
                    new THREE.Vector3(1, 1, 1)
                );
                let fragId = modelBuilder.addFragment(geom, material, transform);
                modelBuilder.changeFragmentsDbId(fragId, i + 100000);
            });
    }
    // ---------------------------------------------------------------------------------------------
    private onSelectionEvent = async (events: { selections: { model: Autodesk.Viewing.Model, dbIdArray: any[] }[] }) => {
        let THREE = await import("three");

        for (var event of events.selections) {
            var model = event.model;
            var dbIds = event.dbIdArray;
            var firstDbId = dbIds[0];
            if (firstDbId > 100000) {
                // This is random data 
                let id = firstDbId - 100000;
                let data = {
                    externalId: id,
                    name: this.drawingData.products[id].name,
                    priv: this.drawingData.products[id],
                    extra: null
                };

                const instanceTree = model.getData().instanceTree;
                const fragList: any = model.getFragmentList();

                let bounds = new THREE.Box3();

                instanceTree.enumNodeFragments(firstDbId, (fragId) => {
                    let box = new THREE.Box3();
                    fragList.getWorldBounds(fragId, box);
                    bounds.union(box);
                }, true);

                data.extra = {
                    bounds: bounds
                };
                this.props.SendPortEvent('Selection', data);

            } else {
                model.getProperties(firstDbId, (results) => {
                    let data: any = _.clone(results);

                    const instanceTree = model.getData().instanceTree;
                    const fragList: any = model.getFragmentList();

                    let bounds = new THREE.Box3();

                    instanceTree.enumNodeFragments(firstDbId, (fragId) => {
                        let box = new THREE.Box3();
                        fragList.getWorldBounds(fragId, box);
                        bounds.union(box);
                    }, true);

                    data.extra = {
                        bounds: bounds
                    };
                    this.props.SendPortEvent('Selection', data);
                }, (results) => {
                    console.error(results);
                });
            }
        }
    }
    // ---------------------------------------------------------------------------------------------
    /**
     * Async viewer event: returns a promise that resolves when the specified event is fired. 
     * Removes event handler automatically
     * @param event 
     */
    viewerEvent(event) {
        return new Promise((resolve, reject) => {
            const handler = (e) => {
                this.viewer.removeEventListener(event, handler)
                resolve(e)
            }
            this.viewer.addEventListener(event, handler)
        });
    }
    // ---------------------------------------------------------------------------------------------
    componentWillUnmount() {
        if (this.viewer) {
            this.viewer.finish();
            this.viewer = null;
            Autodesk.Viewing.shutdown();
        }
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        if (this.viewer && this.viewer.impl) {
            this.viewer.resize();
        }

        return <div
            className="viewer"
            id="MyViewerDiv"
            style={{ width: this.props.width, height: this.props.height, margin: 0, backgroundColor: "#F0F8FF", position: "relative" }}
        >

        </div>;
    }
}
// =================================================================================================
class ForgeViewerSettings extends PureComponent<IWidgetSettingsProps<ForgeViewerData>> {
    constructor(props: IWidgetSettingsProps<ForgeViewerData>) {
        super(props);
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<ForgeViewerData>[] = [{
            name: "",
            elements: [
                {
                    type: SettingsType.String,
                    dataPath: "Document",
                    label: "Document",
                    oneLine: false
                }
            ]
        }];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isDefined(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
        />;
    }
}
// =================================================================================================
export interface Drawing {
    bounds: {
        min: {
            x: number;
            y: number;
            z: number;
        },
        max: {
            x: number;
            y: number;
            z: number;
        }
    },
    products: {
        name: string;
        code: string;
        image: string,
        size: {
            x: number;
            y: number;
            z: number;
        },
        offset: {
            x: number;
            y: number;
            z: number;
        }
    }[]
}
// =================================================================================================
export class ForgeViewerWidget extends IWidgetThingsClass<ForgeViewerData> {
    Render(key: string, width: number, height: number): ReactNode {
        return <ForgeViewer />;
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <ForgeViewerSettings
            key={'settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ---------------------------------------------------------------------------------------------
    private createThingPorts(): WebThingPort[] {
        let ports: WebThingPort[] = [];

        // TODO: Set the Ports
        ports.push(new WebThingPort(
            {
                Name: 'Selection',
                PortKey: PortKeys.Selection,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            },
            WebThingPortType.Data)
        );

        ports.push(new WebThingPort(
            {
                Name: 'Settings',
                PortKey: PortKeys.Settings,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(PortKeys.Settings, data))
        );

        ports.push(new WebThingPort(
            {
                Name: 'Drawing',
                PortKey: PortKeys.Drawing,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(PortKeys.Drawing, data))
        );
        return ports;
    }
    // ---------------------------------------------------------------------------------------------
    updateThing() {
        let webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        // Add thing's ports.
        webThing.Ports = {};
        this.createThingPorts().forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode.GetModule(this.WebModuleKey).UpdateThing(webThing);
    }
    // ---------------------------------------------------------------------------------------------
    initThing() {
        // Create plegma thing.
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing.
        let ports = this.createThingPorts();
        let webThing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = webThing.ThingKey;
    }
    // ---------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): ForgeViewerData {
        return {
            // TODO: Set the default values for the widget
            Document: 'https://dev5emerald.yodiwo.com/Content/forge/shaver/0.svf'
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return ForgeViewerWidget.GetDefaultWidgetDataStatic(); }
    // ---------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        // TODO: Update the widget Description
        return {
            Title: 'Forge Viewer',
            WidgetTitle: '',
            Key: 'ForgeViewerWidget',
            Type: 'ForgeViewerWidget',
            Data: ForgeViewerWidget.GetDefaultWidgetDataStatic(),
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + '/svg/tile.svg'
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        const res = {};

        // expose title if defined
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    }
    // ---------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        // todo: set schema validators
        return { valid: true, errors: "" };
    }
    // ---------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: ForgeViewerData): ForgeViewerData {
        return data;
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultValues() { return {} }
    // ---------------------------------------------------------------------------------------------
}